'use client'

import { usePathname, useRouter } from 'next/navigation'
import { type ReactNode, useEffect } from 'react'
import { runBackOverride, useHasBackOverride } from '@/lib/telegram/back-override'
import { markReplace, parentOf, useCanGoBack } from '@/lib/telegram/nav-stack'

type Surface = 'bg' | 'chat'

// Telegram Mini App provides its own top chrome (back / bot name / menu / close).
// We just render content here. `useTgBackButton` attaches the in-app back action
// to TG's native BackButton API; we don't draw our own bar.
//
// `enableBack` is an explicit opt-out for screens where back makes no sense
// (403, expired session). Back returns to the previous in-app screen; with no
// in-app history (deep link) it goes "up" (quote → group → group list). On the
// group list there is nowhere to go, so TG shows its native close (×).
export function ScreenChrome({
  children,
  enableBack = true,
  surface = 'bg',
}: {
  children: ReactNode
  enableBack?: boolean
  surface?: Surface
}) {
  useTgBackButton(enableBack)
  const bg = surface === 'chat' ? 'bg-q-wall' : 'bg-q-bg'
  // Top inset only exists in fullscreen (Telegram's header is gone): keep content below the status bar / Telegram controls.
  return (
    <div
      className={`flex-1 flex flex-col w-full max-w-[720px] mx-auto pt-[calc(var(--tg-safe-top,0px)+var(--tg-content-safe-top,0px))] ${bg}`}
    >
      {children}
    </div>
  )
}

function useTgBackButton(enabled: boolean) {
  const router = useRouter()
  const pathname = usePathname()
  const canGoBack = useCanGoBack()
  const parent = parentOf(pathname)
  const overlay = useHasBackOverride()
  const show = overlay || (enabled && (canGoBack || parent !== null))
  useEffect(() => {
    const wa = window.Telegram?.WebApp as
      | (import('@/types/tg').TelegramWebApp & {
          BackButton?: {
            show?: () => void
            hide?: () => void
            onClick?: (cb: () => void) => void
            offClick?: (cb: () => void) => void
          }
        })
      | undefined
    const bb = wa?.BackButton
    if (!bb) return
    if (!show) {
      bb.hide?.()
      return
    }
    const back = () => {
      if (runBackOverride()) return
      if (canGoBack) router.back()
      else if (parent) {
        markReplace()
        router.replace(parent as never)
      }
    }
    bb.onClick?.(back)
    bb.show?.()
    return () => {
      bb.offClick?.(back)
      bb.hide?.()
    }
  }, [router, show, canGoBack, parent])
}
